import React, { useRef, useState, KeyboardEvent, useEffect } from 'react';
import axios, { isAxiosError } from 'axios';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';

import {
  REACT_APP_AUTH_CLIENT_ID,
  REACT_APP_AUTH_CLIENT_SECRET,
  REACT_APP_LOGIN_AUTH,
} from '../../../../../constants/envConstants';
import { encrypt } from '../../../../../utils/security.helper';
import useToken from '../../../../../hooks/useToken';
import Welcome from '../Welcome';
import './Styles/mpinStyle.css';

import { getCookieValue, extractPinVerificationDigit } from './helper/helper';
import { FormValues, LoginApiData } from './Interface/mpin-Login.interface';
const MPIN_LENGTH = 4;
const MAX_ATTEMPTS = 3;

const MpinLogin: React.FC = () => {
  const toast = useRef<Toast | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showMpin, setShowMpin] = useState<boolean>(false);
  const { expireToken } = useToken();

  const mpinRef = useRef(Array(MPIN_LENGTH).fill(''));

  const loggedInUser = decodeURIComponent(
    getCookieValue('loggedInUserEmail') || ''
  );

  useEffect(() => {
    localStorage.setItem('Auth_Verify', 'true');
  }, []);

  const validationSchema = Yup.object().shape({
    mpin: Yup.string()
      .length(MPIN_LENGTH, 'Pin must be exactly 4 digits')
      .required('Pin is required'),
  });

  const formik = useFormik<FormValues>({
    initialValues: { mpin: '' },
    validationSchema,
    onSubmit: async values => {
      try {
        setLoading(true);
        const refreshToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('refreshToken='))
          ?.substring('refreshToken='.length);

        const payload = {
          mpin: encrypt(values.mpin),
          refresh_token: refreshToken
            ? refreshToken
            : localStorage.getItem('refresh_token'),
          client_secret: REACT_APP_AUTH_CLIENT_SECRET,
          grant_type: 'refresh_token',
          device_type: 'web',
          secret_client_id: REACT_APP_AUTH_CLIENT_ID,
        };

        const config = {
          headers: {
            'client-id': localStorage.getItem('client_id') ?? '',
            'Content-Type': 'application/json',
            'device-type': 'web_app',
          },
        };

        const response = await axios.post<LoginApiData>(
          `${REACT_APP_LOGIN_AUTH}/auth/login-mpin`,
          payload,
          config
        );
        localStorage.setItem(
          'theme',
          response.data.generateResCode?.ui_theme ?? 'light-blue'
        );
        document.body.classList.add(
          response.data.generateResCode?.ui_theme ?? 'light-blue'
        );
        if (response.status === 200) {
          const accessToken = response.data.generateResCode?.access_token ?? '';
          if (accessToken) localStorage.setItem('access_token', accessToken);
          localStorage.setItem('loginTime', dayjs().utc().format());
          localStorage.setItem(
            'accessTokenExpiryTime',
            response.data.generateResCode?.accessTokenValidUntil ?? ''
          );
          localStorage.removeItem('mpin_attempts');
          localStorage.removeItem('pin_verify');
          localStorage.removeItem('Auth_Verify');
          localStorage.removeItem('sessionPaused');
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: response.data.message ?? 'Login Successful',
          });

          window.location.href = `${window.location.origin}/dashboard`;
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: response.data.message ?? 'Something went wrong',
          });
        }
      } catch (err) {
        if (isAxiosError(err)) {
          if (err.response?.status === 450 || err.response?.status === 404) {
            toast.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: err.response?.data?.message,
            });

            setTimeout(() => expireToken(), 2000);
          }
          const prevAttempts = parseInt(
            localStorage.getItem('mpin_attempts') ?? '0',
            10
          );
          const attempts = prevAttempts + 1;
          localStorage.setItem('mpin_attempts', attempts.toString());

          const attemptsLeft = MAX_ATTEMPTS - attempts;

          if (attempts >= MAX_ATTEMPTS) {
            toast.current?.show({
              severity: 'error',
              summary: 'User Blocked',
              detail:
                'Too many wrong attempts. Please login using Email & password.',
            });
            setTimeout(() => expireToken(), 2000);
          } else {
            toast.current?.show({
              severity: 'error',
              detail: `Invalid PIN, ${attemptsLeft} attempt${attemptsLeft > 1 ? 's' : ''} left.`,
            });
          }
        }
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (formik?.values?.mpin?.length === MPIN_LENGTH) {
      formik.handleSubmit();
    }
  }, [formik.values?.mpin]);

  const handleIconKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleToggle();
    }
  };

  const handleToggle = () => setShowMpin(prev => !prev);

  const handleOtpChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    idx: number
  ) => {
    const digit = extractPinVerificationDigit(e?.target?.value);

    mpinRef.current[idx] = digit;
    e.target.value = digit; // keeps UI stable

    if (formik?.values?.mpin?.length !== MPIN_LENGTH) {
      formik.setFieldTouched('mpin', true, true);
    }
    formik.setFieldValue('mpin', mpinRef.current.join(''));
    if (digit && idx < MPIN_LENGTH - 1) {
      document.getElementById(`otp-${idx + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) => {
    if (e.key === 'Backspace') {
      mpinRef.current[idx] = ''; // clear current box

      if (idx > 0 && !e.currentTarget.value) {
        document.getElementById(`otp-${idx - 1}`)?.focus();
      }
    }

    formik.setFieldValue('mpin', mpinRef.current.join(''));
  };

  return (
    <>
      <Toast ref={toast} />

      <div className="ml-0 LoginContainer flex align-items-center justify-content-center">
        <div>
          <img
            src="/images/circle-background.png"
            alt="Project Force"
            className="circle-bg mb-3"
          />
          <div className="text-center pt-5">
            <img
              src="/images/PF-white-logo.png"
              alt="Project Force"
              className="mb-3"
              style={{ maxWidth: '180px' }}
            />
            <p className="px-3" style={{ maxWidth: '470px', margin: 'auto' }}>
              ProjectsForce puts you in control of your work, keeping both you
              and your clients on-track towards completing the job and getting
              paid.
            </p>
          </div>
          <div className="flex col-12 pb-0">
            <Welcome />
            <div className="flex col-6 flex w-100 align-center wrap px-3 lg:px-5 pt-0 pb-1 ml-0 login-container-right">
              <div className="flex login-right-inner w-12">
                <div className="login-section w-12 py-2 md:py-4 px-2 md:px-4">
                  <h1 className="text-center mt-4 lg:mt-2 md:mt-0 text-xl md:text-2xl ">
                    Enter Your PIN
                  </h1>
                  <form onSubmit={formik.handleSubmit}>
                    <div className="col-12 md:col-12 w-full pl-0 text-center">
                      <span className="p-float-label">
                        <div className="text-base border-round-2xl">
                          Email : <span>{loggedInUser}</span>
                        </div>
                      </span>
                    </div>

                    <div className="col-12 pl-0 mb-3 ml-3 w-full text-center">
                      <label htmlFor="m_pin" className="text-base ml-0">
                        PIN<span style={{ color: 'red' }}>*</span>
                      </label>
                      <div className=" text-center core-input-border">
                        <div className="w-full flex justify-content-center align-items-center gap-2 sm:gap-3 mt-2">
                          {Array.from({ length: MPIN_LENGTH }).map((_, idx) => (
                            <input
                              key={idx}
                              id={`otp-${idx}`}
                              type={showMpin ? 'text' : 'password'}
                              value={mpinRef.current[idx] || ''}
                              maxLength={1}
                              className="otp-input border border-gray-400  text-center border-1 px-0 border-round-sm w-3rem md:w-4rem h-3rem text-base font-normal outline-none focus:outline-none focus:shadow-none"
                              onChange={e => handleOtpChange(e, idx)}
                              onKeyDown={e => handleOtpKeyDown(e, idx)}
                            />
                          ))}
                          <i
                            className={`relative top-0 ml-4 pi ${showMpin ? 'pi-eye-slash' : 'pi-eye'}`}
                            role="button"
                            tabIndex={0}
                            onClick={handleToggle}
                            onKeyDown={handleIconKeyDown}
                          />
                        </div>
                      </div>

                      {formik.touched.mpin && formik.errors.mpin && (
                        <div className="error">{formik.errors.mpin}</div>
                      )}
                    </div>

                    <div className="mt-2 text-center">
                      Login via email & password
                      <span
                        onClick={() => expireToken()}
                        className="text-blue-500 ml-2 cursor-pointer"
                      >
                        Click here
                      </span>
                    </div>

                    <Button
                      icon={loading ? 'pi pi-spin pi-spinner' : ''}
                      type="submit"
                      style={{
                        background: '#0074d9',
                        border: '1px solid #0074d9',
                      }}
                      label="Verify"
                      className="mt-3 w-full"
                      disabled={loading}
                    />

                    <div className="mt-2 text-center">
                      By using ProjectsForce, you agree to our{' '}
                      <a href="https://projectsforce.com/privacy-policy">
                        Privacy Policy
                      </a>
                      .
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MpinLogin;
